// @ts-nocheck
import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { MARKET_INTELLIGENCE_NAICS_QUERY } from "../queries/MI";
import { HgNaics } from "../../types/taxonomy";
import { TAXONOMY_CLIENT } from "../../utils/consts";

interface NaicsQueryPayload {
  naics2012: {
    data: HgNaics[];
  };
}

/** Hits taxonomy-API to grab the NAICS hierarchy then returns ancestry details
 *  @returns a map of each NAICS to all of its ancestors (self-inclusive) and a map of all descendants (self-inclusive)
 */
export function useNaicsAncestorDescendantMap(skip?: boolean) {
  const { data, loading } = useQuery<NaicsQueryPayload>(
    MARKET_INTELLIGENCE_NAICS_QUERY,
    {
      skip,
      context: { clientName: TAXONOMY_CLIENT },
    }
  );

  const allNaics = useMemo(
    () =>
      (data?.naics2012?.data || [])
        .slice()
        .sort((a, b) => (a.code < b.code ? -1 : 1)),
    [data]
  );
  const { ancestorMap, directDescendantMap } = useMemo(() => {
    const ancestorMap = {};
    const directDescendantMap = {};
    for (const naics of allNaics) {
      ancestorMap[naics.id] = getAncestors(naics, allNaics);
      directDescendantMap[naics.id] = getDirectDescendants(naics, allNaics);
    }
    return { ancestorMap, directDescendantMap };
  }, [allNaics]);

  const descendantMap = useMemo(() => {
    const leafDescendantMap = {};
    for (const naics of allNaics) {
      leafDescendantMap[naics.id] = getDescendants(
        naics,
        directDescendantMap,
        allNaics
      );
    }
    return leafDescendantMap;
  }, [allNaics, directDescendantMap]);

  if (loading) {
    return {
      ancestorMap: {},
      leafDescendantMap: {},
      allNaics: [],
      loading: true,
    };
  }
  return {
    ancestorMap: ancestorMap,
    descendantMap: descendantMap,
    allNaics: allNaics,
    loading: loading,
  };
}

function getAncestors(naics: HgNaics, allNaics: HgNaics[]): HgNaics[] {
  const ancestors = [];
  let workingNaics = naics;
  ancestors.push(workingNaics.id);
  while (workingNaics.parentId != "0") {
    const nextAncestor = allNaics.find(
      (naics) => naics.id == workingNaics.parentId
    );
    ancestors.push(nextAncestor.id);
    workingNaics = nextAncestor;
  }
  return ancestors;
}

function getDirectDescendants(
  parentNaics: HgNaics,
  allNaics: HgNaics[]
): HgNaics[] {
  return allNaics.filter((naics) => naics.parentId == parentNaics.id);
}

function getDescendants(
  parentNaics: HgNaics,
  directDescendantMap: Record<string, HgNaics[]>,
  allNaics: HgNaics[]
): HgNaics[] {
  if (!directDescendantMap[parentNaics.id].length) {
    return [parentNaics];
  } else {
    return [
      parentNaics,
      ...directDescendantMap[parentNaics.id]
        .map((naics) => getDescendants(naics, directDescendantMap, allNaics))
        .flat(),
    ];
  }
}
